// AA "Campaigns" / "Ad Groups" / "Keywords" tables — ONE generic component
// for all three levels (design call, superseding the campaigns-only
// AdCampaignsTable this replaces), rendered as TABS inside one card. See
// AdEntityRow's backend docstring for the live field-by-field proof across
// both platforms and all three levels.
//
// Placement (Carl's ruling): lives INSIDE AdvertisingPanel/MetaAdsPanel,
// below the account KPIs — not a separate page/route. That panel renders
// two-up (Google + Meta side by side) whenever a client runs both
// platforms, so this card has to hold up at roughly half viewport width:
// - Tabs, not three stacked tables, keep the card's height bounded
//   regardless of two-up layout — only one table is visible at a time.
// - The table itself scrolls horizontally INSIDE its own container
//   (`overflow-x-auto` below), never the page body, so a half-width column
//   squeeze never blows out the panel's layout.
// - Client-side pagination (25 rows/page) on top of search+sort — keyword
//   sets run into the hundreds of rows (backfill limit raised to 300),
//   unlike campaigns/ad-groups which are usually single-digit-to-dozens
//   and will just show one page.
//
// Each metric cell carries an optional ↑/↓ % period-over-period badge —
// computed by aa_build_fixture.py's entity-diff builder from a real second
// read_client_ads/read_client_keywords pull over the prior-period window,
// never fabricated. An entity with no prior-period counterpart (paused,
// newly launched, or a keyword that just started ranking) simply shows no
// badge on any of its cells, same null-safe convention as
// MultiEngineRankTable.tsx's RankCell.
//
// cost/avg_cpc use a local 2-decimal currency formatter, not the shared
// formatMoney() (primitives.tsx) which rounds to whole dollars for the
// hero KPI tiles — this table's whole point is per-entity fidelity to AA's
// own report (e.g. "$2.35" CPC, "$3,713.94" cost), where whole-dollar
// rounding would hide the real cent-level differences between rows. ctr
// reuses the shared formatPct() at 2 decimals (same reasoning — a bare
// integer round collapses AA's own "0.08%" reference values to "0%"): the
// builder already normalizes both source assets' raw 0-100 ctr to the same
// 0-1 fraction AdsSection.ctr uses (see AdEntityRow's docstring), so no
// other special-casing is needed here.

import { useEffect, useMemo, useState } from "react";
import type { AdEntityRow } from "@/types/clientAnalytics";
import { changeTone, formatNum, formatPct, Pill } from "@/components/client-analytics/primitives";

export interface AdEntityBreakdownTableProps {
  campaigns: AdEntityRow[];
  adGroups: AdEntityRow[];
  // Absent entirely (not just empty) when a platform/page hasn't wired this
  // level -- the tab itself doesn't render rather than showing a
  // permanently-empty tab. Same convention as `keywords` below.
  ads?: AdEntityRow[];
  // Absent entirely (not just empty) for Meta — AA has no keyword-level
  // breakdown for that platform, so the Keywords tab itself doesn't render
  // rather than showing a permanently-empty tab.
  keywords?: AdEntityRow[];
  // Ruling 5b (truncation honesty): true when the backend pull for this
  // level returned EXACTLY its row cap -- i.e. `rows.length` IS the cap,
  // not necessarily the true total. Undefined/false means "not capped, or
  // this level doesn't have a cap concern." When true, the footer caption
  // drops the "of N" framing entirely (the true total is unknowable
  // without pagination) in favor of an honest "pull limit reached — more
  // may exist" -- "Showing 100 of 100 Ads" would render truncation as
  // completeness, which is worse than just saying what's actually true.
  adGroupsCapped?: boolean;
  adsCapped?: boolean;
  platform: string; // "Google Ads" | "Meta Ads" — caption + empty-state text only
  currency?: string;
}

type Level = "campaign" | "adgroup" | "ad" | "keyword";
type SortKey = "name" | "impressions" | "clicks" | "avg_cpc" | "ctr" | "cost";
type SortDir = "asc" | "desc";

// One identity column, in DISPLAY order for its tab. "name" is always the
// row's own AdEntityRow.name (sortable, click-to-filter); "parent"/
// "grandparent" read those same-named AdEntityRow fields verbatim (plain
// display strings, never re-derived). Column ORDER is level-specific, not a
// fixed "ancestors before name" rule -- see tabsFor()'s comment for why the
// Ads level puts its immediate parent (Ad Set) AFTER the name instead of
// before, unlike the Ad Sets level's Campaign column.
type IdentityKey = "grandparent" | "parent" | "name";
interface IdentityColumn {
  key: IdentityKey;
  label: string;
}

interface TabConfig {
  level: Level;
  label: string; // tab button label
  columns: IdentityColumn[]; // exactly one entry has key "name"
}

// Meta calls the adgroup level "Ad Sets", Google calls it "Ad Groups" -- same
// AdEntityRow shape, different platform vocabulary (AA's own report page is
// literally titled "Meta Ads | Ad Sets"). Mislabeling Meta's tab "Ad Groups"
// would read as a copy-paste leftover from the Google table this component
// was generalized from (see file docstring).
//
// Both the Ad Sets and Ads identity columns are Meta-only for now:
// aa_build_fixture.py only backfills AdEntityRow.parent/grandparent for
// Meta rows at those levels -- Google's rows still carry null there (an
// explicit, documented scope decision on the backend side, not an
// oversight). Turning these columns on for Google today would just be a
// column of "—" on every row; wait for that backfill before flipping this
// on for Google too.
//
// Column ORDER per level, confirmed against AA's own reference report
// pages (not assumed): Ad Sets shows "CAMPAIGN | AD SET" (parent BEFORE
// name -- the row's only ancestor leads). Ads shows "CAMPAIGN | AD | AD
// SET" (grandparent BEFORE name, but its immediate parent AFTER -- AA
// leads with the broadest grouping, centers the report's actual subject,
// then trails with the narrower context). Not a mechanical "ancestors
// first" rule; each level's `columns` order is taken from its own
// screenshot, not inferred from the other.
function tabsFor(platform: string): TabConfig[] {
  const isMeta = platform.toLowerCase().includes("meta");
  const tabs: TabConfig[] = [
    { level: "campaign", label: "Campaigns", columns: [{ key: "name", label: "Campaign" }] },
    isMeta
      ? {
          level: "adgroup",
          label: "Ad Sets",
          columns: [
            { key: "parent", label: "Campaign" },
            { key: "name", label: "Ad Set" },
          ],
        }
      : { level: "adgroup", label: "Ad Groups", columns: [{ key: "name", label: "Ad Group" }] },
  ];
  if (isMeta) {
    tabs.push({
      level: "ad",
      label: "Ads",
      columns: [
        { key: "grandparent", label: "Campaign" },
        { key: "name", label: "Ad" },
        { key: "parent", label: "Ad Set" },
      ],
    });
  }
  tabs.push({ level: "keyword", label: "Keywords", columns: [{ key: "name", label: "Keyword" }] });
  return tabs;
}

const COLUMNS: { key: SortKey; label: string; align: "left" | "right" }[] = [
  { key: "impressions", label: "Impressions", align: "right" },
  { key: "clicks", label: "Clicks", align: "right" },
  { key: "avg_cpc", label: "Avg CPC", align: "right" },
  { key: "ctr", label: "CTR", align: "right" },
  { key: "cost", label: "Cost", align: "right" },
];

const PAGE_SIZE = 25;

function formatCurrency2(n: number | null | undefined, currency: string): string {
  if (n === null || n === undefined) return "—";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(n);
}

function sortValue(row: AdEntityRow, key: SortKey): string | number {
  switch (key) {
    case "name":
      return row.name.toLowerCase();
    case "impressions":
      return row.impressions;
    case "clicks":
      return row.clicks;
    case "avg_cpc":
      return row.avg_cpc ?? -Infinity;
    case "ctr":
      return row.ctr ?? -Infinity;
    case "cost":
      return row.cost;
  }
}

// A metric value plus its optional Δ% badge. `metric` selects the badge's
// colour polarity via primitives.tsx's changeTone() -- e.g. "avg_cpc"/"cost"
// diverge from the default "rising=green" read (a CPC increase is bad news,
// a cost increase is neither good nor bad on its own; see changeTone's own
// doc comment for the full live-defect writeup this fixes, task #21). Every
// other column here (impressions/clicks/ctr) omits `metric` and keeps the
// existing higher-is-better sign coloring, matching AA's own report for
// those metrics exactly as before.
function MetricCell({
  value,
  changePct,
  metric,
}: {
  value: string;
  changePct?: number | null;
  metric?: string;
}) {
  const hasBadge = changePct !== null && changePct !== undefined;
  return (
    <span className="inline-flex items-center justify-end gap-1.5 tabular whitespace-nowrap">
      <span className="text-foreground">{value}</span>
      {hasBadge && (
        <Pill tone={changeTone(changePct!, metric)}>
          {changePct! >= 0 ? "↑" : "↓"} {Math.round(Math.abs(changePct!))}%
        </Pill>
      )}
    </span>
  );
}

export function AdEntityBreakdownTable({
  campaigns,
  adGroups,
  ads,
  keywords,
  adGroupsCapped,
  adsCapped,
  platform,
  currency = "USD",
}: AdEntityBreakdownTableProps) {
  const hasAds = ads !== undefined;
  const hasKeywords = keywords !== undefined;
  // Ruling 5b -- only the two levels adset-backend actually caps carry a
  // capped flag; every other level is implicitly "not capped."
  const cappedByLevel: Partial<Record<Level, boolean>> = { adgroup: adGroupsCapped, ad: adsCapped };
  const allTabs = useMemo(() => tabsFor(platform), [platform]);
  const tabs = useMemo(
    () =>
      allTabs.filter((t) => (t.level === "ad" ? hasAds : t.level === "keyword" ? hasKeywords : true)),
    [allTabs, hasAds, hasKeywords],
  );

  const [activeLevel, setActiveLevel] = useState<Level>("campaign");
  const [search, setSearch] = useState("");
  const [sortKey, setSortKey] = useState<SortKey>("cost");
  const [sortDir, setSortDir] = useState<SortDir>("desc");
  const [page, setPage] = useState(0);

  const rowsByLevel: Record<Level, AdEntityRow[]> = {
    campaign: campaigns,
    adgroup: adGroups,
    ad: ads ?? [],
    keyword: keywords ?? [],
  };
  const activeTab = tabs.find((t) => t.level === activeLevel) ?? tabs[0];
  const rows = rowsByLevel[activeTab.level];

  // Search/sort changing the visible set can strand `page` past the new
  // last page — reset to page 0 whenever the active tab, filter, or sort
  // changes rather than showing a blank "page 4 of 1" table.
  useEffect(() => {
    setPage(0);
  }, [activeLevel, search, sortKey, sortDir]);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    const base = term ? rows.filter((r) => r.name.toLowerCase().includes(term)) : rows;
    return [...base].sort((a, b) => {
      const av = sortValue(a, sortKey);
      const bv = sortValue(b, sortKey);
      if (av < bv) return sortDir === "asc" ? -1 : 1;
      if (av > bv) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
  }, [rows, search, sortKey, sortDir]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageRows = filtered.slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE);

  function toggleSort(key: SortKey) {
    if (key === sortKey) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("desc");
    }
  }

  return (
    <div className="rounded-lg border border-border bg-card overflow-hidden">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-4 py-2.5">
        <div className="flex items-center gap-1" role="tablist" aria-label={`${platform} breakdown level`}>
          {tabs.map((tab) => (
            <button
              key={tab.level}
              type="button"
              role="tab"
              aria-selected={activeTab.level === tab.level}
              onClick={() => setActiveLevel(tab.level)}
              className={`rounded-md px-2.5 py-1 text-dash-context font-semibold transition-colors ${
                activeTab.level === tab.level
                  ? "bg-coral text-white"
                  : "text-muted-foreground hover:bg-muted hover:text-foreground"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={`Search ${activeTab.label.toLowerCase()}…`}
          aria-label={`Search ${activeTab.label.toLowerCase()}`}
          className="w-40 rounded-md border border-border bg-background px-2.5 py-1 text-dash-context text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
        />
      </div>
      <div className="max-h-[420px] overflow-y-auto overflow-x-auto">
        <table className="w-full border-collapse text-dash-subtitle">
          <thead className="sticky top-0 z-10 bg-muted">
            <tr className="border-b border-border">
              {activeTab.columns.map((idCol) =>
                idCol.key === "name" ? (
                  <th
                    key={idCol.key}
                    scope="col"
                    aria-sort={sortKey === "name" ? (sortDir === "asc" ? "ascending" : "descending") : undefined}
                    className="px-4 py-2.5 text-left text-dash-label font-semibold uppercase tracking-wide text-muted-foreground cursor-pointer select-none whitespace-nowrap hover:text-foreground"
                    onClick={() => toggleSort("name")}
                  >
                    {idCol.label}
                    {sortKey === "name" ? (sortDir === "asc" ? " ▲" : " ▼") : ""}
                  </th>
                ) : (
                  // Not sortable -- there's no dedicated sort key for the
                  // parent/grandparent entity (AdEntityRow only carries a
                  // display string here, not a real aggregate to sort by).
                  <th
                    key={idCol.key}
                    scope="col"
                    className="px-4 py-2.5 text-left text-dash-label font-semibold uppercase tracking-wide text-muted-foreground whitespace-nowrap"
                  >
                    {idCol.label}
                  </th>
                ),
              )}
              {COLUMNS.map((col) => (
                <th
                  key={col.key}
                  scope="col"
                  aria-sort={sortKey === col.key ? (sortDir === "asc" ? "ascending" : "descending") : undefined}
                  className="px-4 py-2.5 text-right text-dash-label font-semibold uppercase tracking-wide text-muted-foreground cursor-pointer select-none whitespace-nowrap hover:text-foreground"
                  onClick={() => toggleSort(col.key)}
                >
                  {col.label}
                  {sortKey === col.key ? (sortDir === "asc" ? " ▲" : " ▼") : ""}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {pageRows.length === 0 ? (
              <tr>
                <td
                  colSpan={COLUMNS.length + activeTab.columns.length}
                  className="px-4 py-6 text-center text-dash-context text-muted-foreground"
                >
                  {rows.length === 0
                    ? `No ${activeTab.label.toLowerCase()} data yet for ${platform}.`
                    : `No ${activeTab.label.toLowerCase()} match your search.`}
                </td>
              </tr>
            ) : (
              pageRows.map((row) => (
                <tr
                  key={row.entity_id}
                  className="border-b border-border last:border-0 transition-colors hover:bg-muted/60"
                >
                  {activeTab.columns.map((idCol) => {
                    if (idCol.key === "name") {
                      // Keyword tab is the one case with only a "name"
                      // column (no parent/grandparent identity columns) --
                      // `row.parent` there is a pre-formatted "ad group ›
                      // campaign" breadcrumb, kept as stacked subtext since
                      // it isn't a clean single value a real column could
                      // hold. Harmless no-op for campaign rows (parent is
                      // always null there).
                      const showBreadcrumbSubtext = activeTab.columns.length === 1;
                      return (
                        <td key="name" className="px-4 py-2.5 font-medium text-foreground">
                          <button
                            type="button"
                            onClick={() => setSearch(row.name)}
                            title={`Filter this table to "${row.name}"`}
                            className="whitespace-nowrap text-coral hover:text-coral-hover hover:underline"
                          >
                            {row.name}
                          </button>
                          {showBreadcrumbSubtext && row.parent && (
                            <div className="text-dash-context font-normal text-muted-foreground whitespace-nowrap">
                              {row.parent}
                            </div>
                          )}
                        </td>
                      );
                    }
                    // "—" (not blank) until adset-backend backfills
                    // parent/grandparent for every row at this level --
                    // same never-blank convention as formatCurrency2/
                    // formatNum elsewhere in this table, rather than a
                    // silently empty cell that reads as a bug.
                    const value = idCol.key === "parent" ? row.parent : row.grandparent;
                    return (
                      <td key={idCol.key} className="px-4 py-2.5 text-muted-foreground">
                        <div className="whitespace-nowrap">{value || "—"}</div>
                      </td>
                    );
                  })}
                  <td className="px-4 py-2.5 text-right">
                    <MetricCell value={formatNum(row.impressions)} changePct={row.impressions_change_pct} />
                  </td>
                  <td className="px-4 py-2.5 text-right">
                    <MetricCell value={formatNum(row.clicks)} changePct={row.clicks_change_pct} />
                  </td>
                  <td className="px-4 py-2.5 text-right">
                    <MetricCell
                      value={formatCurrency2(row.avg_cpc, currency)}
                      changePct={row.avg_cpc_change_pct}
                      metric="avg_cpc"
                    />
                  </td>
                  <td className="px-4 py-2.5 text-right">
                    {/* 2 decimals -- per-entity CTR is routinely sub-1%
                        (AA reference: "0.08%"); the shared formatPct()'s
                        default whole-percent rounding would collapse it to
                        "0%", same fidelity concern formatCurrency2 above
                        already accounts for on avg_cpc/cost. */}
                    <MetricCell value={formatPct(row.ctr, 2)} changePct={row.ctr_change_pct} />
                  </td>
                  <td className="px-4 py-2.5 text-right">
                    <MetricCell
                      value={formatCurrency2(row.cost, currency)}
                      changePct={row.cost_change_pct}
                      metric="cost"
                    />
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      <div className="flex items-center justify-between gap-3 border-t border-border px-4 py-2 text-dash-context text-muted-foreground">
        <div>
          {/* House wording is "Showing {n} of {m}" -- no unit word -- one
              template for every state, not three separate sentences. This
              used to branch into a capped-specific "(pull limit reached —
              more may exist)" sentence, a single-page "Showing 6 of 6 Rows"
              deliberately mirroring AA's own report-page phrasing, and a
              multi-page "X–Y of N {level}" range. Carl overrode the AA-parity
              call: the "Page X of Y" control right below this line already
              shows page position, so the range added nothing a plain count
              didn't already say, and three prose variants for what's really
              one fact (how many of the known/fetched set are visible) read
              as three different features. The capped/paged distinction is
              genuine (ruling 5b: a capped pull's total is a floor, not an
              exact count) and still shown -- just in the NUMBERS, via the
              same "+" convention LandingPagesTable.tsx uses for its own
              capped total, not in different prose. */}
          {`Showing ${pageRows.length} of ${filtered.length}${cappedByLevel[activeTab.level] ? "+" : ""}`}
        </div>
        {pageCount > 1 && (
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={page === 0}
              className="rounded-md border border-border px-2 py-0.5 font-semibold disabled:opacity-40 hover:bg-muted disabled:hover:bg-transparent"
            >
              Prev
            </button>
            <span>
              Page {page + 1} of {pageCount}
            </span>
            <button
              type="button"
              onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))}
              disabled={page >= pageCount - 1}
              className="rounded-md border border-border px-2 py-0.5 font-semibold disabled:opacity-40 hover:bg-muted disabled:hover:bg-transparent"
            >
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
